import { useCallback, useRef, useState } from "react";
import { useDropzone } from "react-dropzone";
import { motion, AnimatePresence } from "framer-motion";
import { GlassMorphCard } from "../ui/GlassMorphCard";
import { requestVideoOverview, OverviewResponse, API_BASE } from "../../lib/api";

// Removed "web" from the options
type InputType = "prompt" | "youtube" | "file";

function ensureAbsoluteUrl(u?: string) {
  if (!u) return "";
  if (u.startsWith('http://') || u.startsWith('https://')) return u;
  if (u.startsWith('/')) return window.location.origin + u;
  return u;
}

// Stunning 3D Orbital Loader Merged with App Theme
const SynthesisOrb = () => (
  <div className="relative w-72 h-72 flex items-center justify-center perspective-[1200px] mx-auto my-8">
    {/* Outer rotating ring */}
    <motion.div animate={{ rotateX: 360, rotateY: 180, rotateZ: 90 }} transition={{ duration: 5, repeat: Infinity, ease: "linear" }} 
      className="absolute w-full h-full border-[3px] border-t-electric border-b-magenta border-l-transparent border-r-transparent rounded-full opacity-60 shadow-[0_0_30px_rgba(139,95,255,0.4)]" />
    
    {/* Inner fast ring */}
    <motion.div animate={{ rotateY: -360, rotateZ: 360, rotateX: 180 }} transition={{ duration: 4, repeat: Infinity, ease: "linear" }} 
      className="absolute w-[75%] h-[75%] border-[4px] border-l-electric border-r-electric border-t-transparent border-b-transparent rounded-full opacity-80 shadow-[0_0_20px_rgba(217,70,239,0.5)]" />
    
    {/* Core stability ring */}
    <motion.div animate={{ rotateZ: 360 }} transition={{ duration: 8, repeat: Infinity, ease: "linear" }} 
      className="absolute w-[50%] h-[50%] border border-white/40 border-dashed rounded-full opacity-50" />
    
    {/* Core glowing orb */}
    <div className="absolute w-20 h-20 bg-white rounded-full blur-[10px] animate-pulse" style={{ boxShadow: "0 0 60px 30px rgba(139,95,255,0.7)" }} />
    
    {/* Core Icon */}
    <div className="absolute text-3xl z-10 drop-shadow-[0_0_10px_rgba(255,255,255,1)]">✨</div>
  </div>
);

export default function VideoOverview() {
  const [inputType, setInputType] = useState<InputType>("prompt");
  const [prompt, setPrompt] = useState("");
  const [url, setUrl] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [duration, setDuration] = useState(1.0);
  const [busy, setBusy] = useState(false);
  const [resp, setResp] = useState<OverviewResponse | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const controllerRef = useRef<AbortController | null>(null);

  const onDrop = useCallback((files: File[]) => setFile(files?.[0] ?? null), []);
  const dz = useDropzone({ onDrop, multiple: false });

  const canSubmit = inputType === "prompt" ? !!prompt.trim() : inputType === "file" ? !!file : !!url.trim();

  const start = async () => {
    if (!canSubmit) return;
    setBusy(true); setErr(null); setResp(null);
    if (controllerRef.current) controllerRef.current.abort();
    controllerRef.current = new AbortController();
    try {
      const data = await requestVideoOverview({
        input: inputType, prompt: inputType === "prompt" ? prompt : undefined,
        url: inputType === "youtube" ? url : undefined,
        file: inputType === "file" && file ? file : undefined, duration_min: duration, make_video: true, voice_hint: "nova"
      }, controllerRef.current.signal);
      console.log('[UI] Set response:', data);
      setResp(data);
    } catch (e: any) {
      if (e.name !== "AbortError") setErr(e.message || String(e));
    } finally { setBusy(false); }
  };

  const videoSrc = resp?.summary_video_url ? ensureAbsoluteUrl(resp.summary_video_url) : null;
  if (videoSrc) console.log('[UI] Render VideoSrc:', videoSrc);

  return (
    <div className="h-full flex flex-col lg:flex-row gap-8 items-stretch">
      
      {/* Left Configuration Panel */}
      <GlassMorphCard className="w-full lg:w-[400px] flex flex-col bg-charcoal/60 backdrop-blur-3xl border border-white/10 shadow-2xl rounded-3xl p-6">
        <div className="flex-1">
          <h3 className="text-sm font-bold uppercase tracking-widest text-white/40 mb-4">Content Source</h3>
          
          <div className="flex gap-2 mb-6 p-1.5 bg-black/40 rounded-xl border border-white/5">
            {(["prompt","youtube","file"] as InputType[]).map(t => (
              <button key={t} onClick={() => setInputType(t)}
                className={`flex-1 py-2.5 text-xs font-bold uppercase tracking-wider rounded-lg transition-all duration-300 ${inputType===t ? "bg-gradient-to-r from-electric to-magenta text-white shadow-glow" : "hover:bg-white/5 text-white/40 hover:text-white"}`}>
                {t}
              </button>
            ))}
          </div>

          <div className="mb-8">
            {inputType==="prompt" && <textarea value={prompt} onChange={e=>setPrompt(e.target.value)} className="w-full h-48 bg-black/30 border border-white/10 rounded-2xl p-5 text-sm outline-none focus:border-electric focus:ring-1 focus:ring-electric transition-all resize-none shadow-inner" placeholder="E.g., Explain the concept of Quantum Entanglement using simple analogies..." />}
            {inputType==="youtube" && <input value={url} onChange={e=>setUrl(e.target.value)} className="w-full bg-black/30 border border-white/10 rounded-2xl p-5 text-sm outline-none focus:border-electric focus:ring-1 focus:ring-electric transition-all shadow-inner" placeholder="Paste YouTube URL here..." />}
            {inputType==="file" && (
              <div {...dz.getRootProps()} className={`h-48 rounded-2xl border-2 border-dashed flex flex-col items-center justify-center p-6 text-center cursor-pointer transition-all duration-300 ${dz.isDragActive ? "border-electric bg-electric/10 scale-[1.02]" : "border-white/10 bg-black/30 hover:border-white/30 hover:bg-black/50"}`}>
                <input {...dz.getInputProps()} />
                <svg className="w-10 h-10 mb-3 text-white/30" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" /></svg>
                <div className="text-sm text-white/50">{file ? <span className="text-electric font-semibold">{file.name}</span> : "Drag & drop PDF, Word, or Audio file"}</div>
              </div>
            )}
          </div>

          <div className="bg-black/30 rounded-2xl p-5 border border-white/5">
            <div className="flex justify-between text-xs font-bold uppercase tracking-widest text-white/40 mb-4">
              <span>Duration Target</span>
              <span className="text-white">{duration.toFixed(1)} mins</span>
            </div>
            <input type="range" min={0.5} max={5} step={0.5} value={duration} onChange={e=>setDuration(parseFloat(e.target.value))} className="w-full h-2 bg-white/10 rounded-lg appearance-none cursor-pointer accent-electric" />
          </div>
        </div>

        <button disabled={!canSubmit || busy} onClick={start} className="mt-8 w-full py-4 rounded-2xl bg-gradient-to-r from-electric to-magenta font-extrabold text-sm uppercase tracking-widest hover:shadow-[0_0_40px_rgba(139,95,255,0.4)] disabled:opacity-40 disabled:cursor-not-allowed transition-all duration-300 hover:scale-[1.02] active:scale-95">
          {busy ? "Processing Request..." : "Generate Production"}
        </button>
      </GlassMorphCard>

      {/* Right Canvas Panel */}
      <GlassMorphCard className="flex-1 flex flex-col items-center justify-center min-h-[600px] relative overflow-hidden bg-charcoal/40 backdrop-blur-3xl border border-white/10 shadow-2xl rounded-3xl p-2">
        <AnimatePresence mode="wait">
          
          {/* Idle State */}
          {!busy && !resp && !err && (
            <motion.div key="idle" initial={{opacity:0, scale:0.95}} animate={{opacity:1, scale:1}} exit={{opacity:0, y:-20}} className="text-center text-white/30 flex flex-col items-center">
              <div className="w-32 h-32 mb-6 rounded-full border border-white/5 flex items-center justify-center bg-black/20 shadow-inner">
                <svg className="w-12 h-12 opacity-50" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
              </div>
              <h3 className="text-2xl font-bold tracking-tight text-white/50 mb-2">Studio Canvas Awaiting Input</h3>
              <p className="text-sm font-light max-w-sm leading-relaxed">Provide a prompt, document, or YouTube link on the left panel to synthesize a high-fidelity educational video.</p>
            </motion.div>
          )}

          {/* 3D Loading State */}
          {busy && (
            <motion.div key="loading" initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0, scale:1.05}} className="absolute inset-0 flex flex-col items-center justify-center bg-black/40 backdrop-blur-md z-10">
              <SynthesisOrb />
              <div className="text-2xl font-extrabold tracking-[0.2em] mt-10 animate-pulse text-transparent bg-clip-text bg-gradient-to-r from-electric to-magenta">
                SYNTHESIZING REALITY
              </div>
              <div className="flex gap-8 mt-6 text-xs font-bold tracking-widest text-white/30 uppercase">
                 <span className="animate-bounce" style={{animationDelay:"0ms"}}>1. Extracting Context</span>
                 <span className="animate-bounce" style={{animationDelay:"300ms"}}>2. Rendering 3D</span>
                 <span className="animate-bounce" style={{animationDelay:"600ms"}}>3. Mastering Audio</span>
              </div>
            </motion.div>
          )}

          {/* Error State */}
          {err && !busy && (
            <motion.div key="error" initial={{opacity:0, scale:0.95}} animate={{opacity:1, scale:1}} className="text-center text-red-400 p-10 border border-red-500/20 rounded-3xl bg-red-500/10 max-w-lg shadow-[0_0_50px_rgba(239,68,68,0.1)]">
              <div className="w-16 h-16 mx-auto bg-red-500/20 rounded-full flex items-center justify-center mb-4">
                 <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>
              </div>
              <h3 className="font-bold text-2xl mb-3 text-white">Synthesis Failed</h3>
              <p className="text-sm opacity-90 leading-relaxed font-mono bg-black/30 p-4 rounded-xl overflow-x-auto text-left">{err}</p>
            </motion.div>
          )}

          {/* Success Player State */}
          {resp?.summary_video_url && videoSrc && !busy && (
            <motion.div key="player" initial={{opacity:0, scale:0.95, y:20}} animate={{opacity:1, scale:1, y:0}} transition={{type:"spring", damping:25, stiffness:200}} className="w-full h-full flex flex-col p-2">
              <div className="flex-1 w-full rounded-2xl overflow-hidden shadow-[0_30px_60px_rgba(0,0,0,0.6)] border border-white/10 bg-black flex items-center justify-center relative group">
                <video key={videoSrc} controls autoPlay className="max-w-full max-h-full w-full h-full object-contain bg-black">
                  <source src={videoSrc} type="video/mp4" />
                </video>
              </div>
              
              <div className="mt-4 p-5 glass rounded-2xl flex flex-col sm:flex-row justify-between items-center gap-4 bg-white/5 border border-white/10">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-green-500/20 flex items-center justify-center border border-green-500/30">
                    <svg className="w-6 h-6 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                  </div>
                  <div>
                    <h4 className="font-bold text-lg text-white">Master Render Complete</h4>
                    <p className="text-xs font-bold tracking-widest text-white/40 uppercase mt-1">Duration: {resp.final_duration_s}s • High Fidelity</p>
                  </div>
                </div>
                
                <a href={videoSrc} download className="w-full sm:w-auto px-8 py-3 bg-white text-black hover:bg-gray-200 rounded-xl text-sm font-bold tracking-wide transition-all shadow-[0_0_20px_rgba(255,255,255,0.2)] hover:shadow-[0_0_30px_rgba(255,255,255,0.4)] hover:scale-105 flex items-center justify-center gap-2">
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
                  Save MP4
                </a>
              </div>
            </motion.div>
          )}

        </AnimatePresence>
      </GlassMorphCard>
    </div>
  );
}
